import { esEntries } from './es2017';
import { pageContext } from './page_context';
import { getPathWithQueryString } from './browser';

/**
 * Iterate over an Object of urlParams to return a query string
 * @param {String} baseUrl BaseUrl being appended to
 * @param {Object} urlParams Object of url Parameters - NOTE: Assumes already encoded
 * @returns {String} Query string with urlParams
 */
export const buildUrlParams = (baseUrl, urlParams) => {
    let queryString = baseUrl.includes('?') ? '&' : '?';

    for (const [key, value] of esEntries(urlParams)) {
        // remove null, undefined, empty string as params
        if (value || value === false) {
            queryString += `${key}=${value}&`;
        }
    }
    return queryString.slice(0, -1);
};

/**
 * Builds url with appended Params and put params before anchor id if exists
 * @param {String} baseUrl BaseUrl being appended to
 * @param {Object} urlParams Object of url Parameters
 * @returns {String} baseUrl with appended urlParams
 */
export const buildUrlWithParams = (baseUrl, urlParams) => {
    if (urlParams && typeof urlParams === 'object') {
        if (baseUrl.includes('#')) {
            const anchorId = baseUrl.substr(baseUrl.lastIndexOf('#'), baseUrl.length);
            return baseUrl.replace(anchorId, '') + buildUrlParams(baseUrl, urlParams) + anchorId;
        }
        return `${baseUrl}${buildUrlParams(baseUrl, urlParams)}`;
    }
    return `${baseUrl}`;
};

/**
 * onClick handler to take user to go to a internal url without pushing to history
 * @useCase: go to a journey-like temp page but don't want user to be able to go back to it user back/forward button
 * @param {Object} event - the onClick event object
 * @param {String} url - the destination url
 * @param {String} queryString - the extra queryString to append, e.g. `isEdit=true`
 */
export const onReplaceLinkClick = (event, url, queryString) => {
    event.preventDefault();
    const browserHistory = pageContext.getProps().browserHistory;
    const urlWithOriginalParams =  getPathWithQueryString(url);
    const redirectUrl = appendQueryString(urlWithOriginalParams, queryString);
    browserHistory.replace(redirectUrl);
};

/**
 * Append the queryString at the end of the url, adding '?' or '&'
 * @param {String} url - the url to be appended on
 * @param {String} queryString - the extra queryString to append, e.g. `isEdit=true`
 * @returns {String} the output url after appending the queryString
 */
export const appendQueryString = (url, queryString) => {
    if (!queryString) return url;
    return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
};

/**
 * Remove a particular queryString from url, also clean up '&' and '?' so we don't have a invalid url
 * @param {String} url - the url to be appended on
 * @param {String} queryString - the extra queryString to append, e.g. `isEdit=true`
 * @returns {String} the output url after removing the queryString
 */
export const removeQueryString = (url, queryString) => {
    if (!queryString) return url;
    return url.replace(`&${queryString}`, '').replace(queryString, '').replace('&&', '&').replace('?&', '?');
};

/**
 * Return a value for the supplied key within a querystring
 * @param {String} key - the key to find within the querystring
 * @returns {String} the value of the key in the querystring
 */
export const getQueryVariable = (key) => {
    if (typeof window === 'undefined') return false;
    const query = window.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i += 1) {
        const pair = vars[i].split('=');
        if (pair[0] === key) { return pair[1]; }
    }
    return false;
};
